import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";

/**
 * Shared vendor / airline selector used by both AddSegmentDialog and
 * EditSegmentDialog. Filters the vendor list by the current segment type
 * (with travel-aggregators always allowed) and exposes a "+ Add Custom"
 * button when nothing is selected.
 *
 * The flight-only "blue Lookup card" in AddSegmentDialog renders its own
 * (visually different) vendor select, so this component only stamps the
 * neutral version that's used everywhere else (Add for non-flight, Edit
 * for all types).
 */
export function VendorSelectField({
  segmentType,
  vendors,
  selectedVendor,
  onVendorChange,
  onAddCustom,
  required = true,
  testIdPrefix = "vendor",
}) {
  const isFlight = segmentType === "flight";
  const filtered = vendors
    .filter((vendor) => {
      if (vendor.is_travel_aggregator) return true;
      switch (segmentType) {
        case "flight":
          return vendor.type === "flight";
        case "train":
          return vendor.type === "train" || vendor.type === "other";
        case "bus":
          return vendor.type === "bus" || vendor.type === "other";
        case "accommodation":
          return vendor.type === "accommodation" || vendor.type === "other";
        case "cruise":
          return vendor.type === "cruise" || vendor.type === "other";
        case "car_rental":
          return vendor.type === "car_rental" || vendor.type === "other";
        case "rideshare":
          return vendor.type === "rideshare" || vendor.type === "other";
        case "activity":
        case "event":
          return vendor.type === "activity" || vendor.type === "other";
        default:
          return vendor.type === "other";
      }
    })
    .sort((a, b) => {
      if (a.is_travel_aggregator && !b.is_travel_aggregator) return -1;
      if (!a.is_travel_aggregator && b.is_travel_aggregator) return 1;
      return a.name.localeCompare(b.name);
    });

  const label = isFlight ? "Airline" : "Vendor";
  const placeholder = `Select ${isFlight ? "an airline" : "a vendor"}...`;
  const selectTestId = `${testIdPrefix}-vendor-select`;

  return (
    <div className="space-y-3">
      <div className="grid gap-2">
        <Label htmlFor={selectTestId}>
          {label} {required && "*"}
        </Label>
        <Select value={selectedVendor} onValueChange={onVendorChange}>
          <SelectTrigger id={selectTestId} data-testid={selectTestId}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent position="popper" className="z-[100]">
            {filtered.map((vendor) => (
              <SelectItem key={vendor.id} value={vendor.id}>
                {vendor.name} {vendor.is_travel_aggregator && "🌐"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedVendor && (
        <div className="text-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAddCustom}
            className="text-sky-600 hover:text-sky-700 border-sky-300 hover:border-sky-400"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Custom {label}
          </Button>
          <p className="text-xs text-gray-500 mt-1">
            Can&apos;t find the {label.toLowerCase()} you&apos;re looking for?
          </p>
        </div>
      )}
    </div>
  );
}

export default VendorSelectField;
